/**
 * Kids Learning Zone - Game Configuration Catalogue
 *
 * 110 complete game configurations:
 *   - English:    10 base + 20 extra = 30
 *   - Math:       10 base + 20 extra = 30
 *   - Life Skills: 10 base + 15 extra = 25
 *   - Science:    15 base
 *   - Creativity: 10
 *
 * Adapted from the React Native reference implementation for the web app.
 *
 * Each entry describes a game card visible on the hub and carries enough
 * metadata + content for KidsGameScreen to load the correct template at runtime.
 *
 * `template` is a key that maps to a React.lazy() dynamic import inside
 * gameRegistry.js (e.g. 'multiple-choice' -> ../templates/MultipleChoiceTemplate).
 *
 * Content shapes by template type:
 *   multiple-choice  -> content.questions[{ question, options, correctIndex, concept, hint }]
 *   true-false       -> content.statements[{ text, answer, concept, explanation }]
 *   fill-blank       -> content.questions[{ text, blank, options, concept, hint }]
 *   match-pairs      -> content.pairs[{ left, right, concept }]
 *   memory-flip      -> content.pairs[{ id, front, match, concept }], gridColumns
 *   counting         -> content.rounds[{ count, icon, color, concept, label }]
 *   sequence-order   -> content.sequences[{ items[], concept }]
 *   word-build       -> content.words[{ word, hint, concept, extraLetters }]
 *   drag-to-zone     -> content.zones[], content.items[]
 *   timed-rush       -> content.timeLimit, content.questions[]
 *   story-builder    -> content.story.{ start, scenes }
 *   simulation       -> content.scenario.{ title, concept, startingMoney, items[], goal }
 *   puzzle-assemble  -> content.puzzleImage, content.pieces[]
 *   spot-difference  -> content.imageA, content.imageB, content.differences[]
 */

import ENGLISH_GAMES_EXTRA from './englishGamesExtra';
import MATH_GAMES_EXTRA from './mathGamesExtra';
import LIFE_SKILLS_GAMES_EXTRA from './lifeSkillsGamesExtra';
import CREATIVITY_GAMES from './creativityGames';

// ============================================================================
// ENGLISH GAMES (10)
// ============================================================================

const ENGLISH_GAMES = [
  // 1. Animal Spelling Bee (word-build)
  {
    id: 'eng-spell-animals-01',
    title: 'Animal Spelling Bee',
    category: 'english',
    subcategory: 'spelling',
    template: 'word-build',
    ageRange: [5, 8],
    difficulty: 2,
    icon: 'bee',
    color: '#FF6B6B',
    estimatedMinutes: 4,
    questionsPerSession: 10,
    learningObjectives: ['spelling', 'vocabulary'],
    tags: ['animals', 'nature'],
    content: {
      words: [
        { word: 'tiger', hint: 'A big striped cat that lives in the jungle', concept: 'spell:tiger', extraLetters: 3 },
        { word: 'elephant', hint: 'The largest land animal with a long trunk', concept: 'spell:elephant', extraLetters: 3 },
        { word: 'giraffe', hint: 'The tallest animal with a very long neck', concept: 'spell:giraffe', extraLetters: 3 },
        { word: 'dolphin', hint: 'A friendly sea creature that loves to jump', concept: 'spell:dolphin', extraLetters: 3 },
        { word: 'penguin', hint: 'A black and white bird that cannot fly but can swim', concept: 'spell:penguin', extraLetters: 3 },
        { word: 'monkey', hint: 'A playful animal that swings from trees', concept: 'spell:monkey', extraLetters: 2 },
        { word: 'rabbit', hint: 'A furry animal with long ears that hops', concept: 'spell:rabbit', extraLetters: 2 },
        { word: 'parrot', hint: 'A colorful bird that can talk like a human', concept: 'spell:parrot', extraLetters: 2 },
      ],
    },
    rewards: { starsPerCorrect: 1, bonusThreshold: 8, bonusStars: 3 },
    threeR: { measuresRetention: true, measuresRecall: true, measuresRegistration: true },
  },

  // 2. CVC Word Rhymes (match-pairs)
  {
    id: 'eng-phon-cvc-06',
    title: 'CVC Word Rhymes',
    category: 'english',
    subcategory: 'phonics',
    template: 'match-pairs',
    ageRange: [4, 6],
    difficulty: 1,
    icon: 'music-note',
    color: '#FF6B6B',
    estimatedMinutes: 3,
    questionsPerSession: 8,
    learningObjectives: ['phonics', 'rhyming', 'reading'],
    tags: ['cvc', 'rhyming', 'beginner'],
    content: {
      pairs: [
        { left: 'cat', right: 'hat', concept: 'phonics:cvc-cat' },
        { left: 'big', right: 'pig', concept: 'phonics:cvc-big' },
        { left: 'hot', right: 'pot', concept: 'phonics:cvc-hot' },
        { left: 'run', right: 'sun', concept: 'phonics:cvc-run' },
        { left: 'bed', right: 'red', concept: 'phonics:cvc-bed' },
        { left: 'cup', right: 'pup', concept: 'phonics:cvc-cup' },
        { left: 'sit', right: 'fit', concept: 'phonics:cvc-sit' },
        { left: 'log', right: 'dog', concept: 'phonics:cvc-log' },
      ],
    },
    rewards: { starsPerCorrect: 1, bonusThreshold: 8, bonusStars: 3 },
    threeR: { measuresRetention: true, measuresRecall: true, measuresRegistration: true },
  },

  // 3. Home Items Quiz (multiple-choice)
  {
    id: 'eng-vocab-home-11',
    title: 'Home Items Quiz',
    category: 'english',
    subcategory: 'vocabulary',
    template: 'multiple-choice',
    ageRange: [4, 6],
    difficulty: 1,
    icon: 'home',
    color: '#FF6B6B',
    estimatedMinutes: 4,
    questionsPerSession: 8,
    learningObjectives: ['vocabulary', 'comprehension'],
    tags: ['home', 'everyday', 'objects'],
    content: {
      questions: [
        { question: 'What do you use to sweep the floor?', options: ['Broom', 'Spoon', 'Pillow', 'Cup'], correctIndex: 0, concept: 'vocab:broom', hint: 'It has a long handle and bristles at the bottom' },
        { question: 'What do you sleep on at night?', options: ['Table', 'Chair', 'Bed', 'Shelf'], correctIndex: 2, concept: 'vocab:bed', hint: 'It is soft and has a pillow and blanket' },
        { question: 'What do you use to eat soup?', options: ['Fork', 'Knife', 'Plate', 'Spoon'], correctIndex: 3, concept: 'vocab:spoon', hint: 'It is round and can hold liquid' },
        { question: 'Where do you keep your clothes?', options: ['Oven', 'Wardrobe', 'Bathtub', 'Sink'], correctIndex: 1, concept: 'vocab:wardrobe', hint: 'It is a tall piece of furniture with hangers inside' },
        { question: 'What do you use to see yourself?', options: ['Window', 'Mirror', 'Door', 'Curtain'], correctIndex: 1, concept: 'vocab:mirror', hint: 'It reflects your image back to you' },
        { question: 'What keeps food cold and fresh?', options: ['Stove', 'Microwave', 'Refrigerator', 'Toaster'], correctIndex: 2, concept: 'vocab:refrigerator', hint: 'It is a large box in the kitchen that feels cold inside' },
        { question: 'What do you use to dry yourself after a bath?', options: ['Blanket', 'Towel', 'Curtain', 'Rug'], correctIndex: 1, concept: 'vocab:towel', hint: 'It is soft and hangs in the bathroom' },
        { question: 'What do you use to light up a dark room?', options: ['Fan', 'Lamp', 'Clock', 'Vase'], correctIndex: 1, concept: 'vocab:lamp', hint: 'It has a bulb inside that glows' },
      ],
    },
    rewards: { starsPerCorrect: 1, bonusThreshold: 8, bonusStars: 3 },
    threeR: { measuresRetention: true, measuresRecall: true, measuresRegistration: true },
  },

  // 4. Nature Words Quiz (multiple-choice)
  {
    id: 'eng-vocab-nature-12',
    title: 'Nature Words Quiz',
    category: 'english',
    subcategory: 'vocabulary',
    template: 'multiple-choice',
    ageRange: [4, 6],
    difficulty: 1,
    icon: 'tree',
    color: '#FF6B6B',
    estimatedMinutes: 4,
    questionsPerSession: 8,
    learningObjectives: ['vocabulary', 'comprehension', 'nature'],
    tags: ['nature', 'environment'],
    content: {
      questions: [
        { question: 'What is a very tall plant with a trunk and branches?', options: ['Flower', 'Grass', 'Tree', 'Bush'], correctIndex: 2, concept: 'vocab:tree', hint: 'Birds build nests in it' },
        { question: 'What is a large body of flowing water?', options: ['Pond', 'Puddle', 'Lake', 'River'], correctIndex: 3, concept: 'vocab:river', hint: 'It flows from mountains to the sea' },
        { question: 'What is a very high landform with a peak?', options: ['Hill', 'Mountain', 'Valley', 'Plain'], correctIndex: 1, concept: 'vocab:mountain', hint: 'It can have snow on top and takes a long time to climb' },
        { question: 'What falls from the sky when clouds are heavy?', options: ['Snow', 'Rain', 'Wind', 'Sunshine'], correctIndex: 1, concept: 'vocab:rain', hint: 'It is made of water drops' },
        { question: 'What is the star that gives us light during the day?', options: ['Moon', 'Star', 'Sun', 'Cloud'], correctIndex: 2, concept: 'vocab:sun', hint: 'It is very bright and warm' },
        { question: 'What is a large area of sand with very little water?', options: ['Forest', 'Desert', 'Ocean', 'Jungle'], correctIndex: 1, concept: 'vocab:desert', hint: 'Camels live here and it is very hot and dry' },
        { question: 'What is the large body of salt water on Earth?', options: ['Lake', 'Pond', 'River', 'Ocean'], correctIndex: 3, concept: 'vocab:ocean', hint: 'Whales and sharks swim in it' },
        { question: 'What animal builds a web to catch insects?', options: ['Ant', 'Bee', 'Spider', 'Butterfly'], correctIndex: 2, concept: 'vocab:spider', hint: 'It has eight legs' },
      ],
    },
    rewards: { starsPerCorrect: 1, bonusThreshold: 8, bonusStars: 3 },
    threeR: { measuresRetention: true, measuresRecall: true, measuresRegistration: true },
  },

  // 5. Morning Routine (sequence-order)
  {
    id: 'eng-story-morning-16',
    title: 'Morning Routine',
    category: 'english',
    subcategory: 'sequencing',
    template: 'sequence-order',
    ageRange: [4, 7],
    difficulty: 1,
    icon: 'weather-sunny',
    color: '#FF6B6B',
    estimatedMinutes: 3,
    questionsPerSession: 8,
    learningObjectives: ['sequencing', 'comprehension', 'daily-routines'],
    tags: ['routine', 'daily-life', 'ordering'],
    content: {
      sequences: [
        { items: ['Wake up', 'Brush teeth', 'Take a bath', 'Get dressed', 'Eat breakfast', 'Pack schoolbag', 'Put on shoes', 'Go to school'], concept: 'sequence:morning-routine' },
      ],
    },
    rewards: { starsPerCorrect: 1, bonusThreshold: 8, bonusStars: 3 },
    threeR: { measuresRetention: true, measuresRecall: true, measuresRegistration: true },
  },

  // 6. Is It a Noun? (true-false)
  {
    id: 'eng-gram-nouns-19',
    title: 'Is It a Noun?',
    category: 'english',
    subcategory: 'grammar',
    template: 'true-false',
    ageRange: [6, 8],
    difficulty: 2,
    icon: 'book-open-variant',
    color: '#FF6B6B',
    estimatedMinutes: 4,
    questionsPerSession: 10,
    learningObjectives: ['grammar', 'nouns', 'parts-of-speech'],
    tags: ['grammar', 'nouns'],
    content: {
      statements: [
        { text: '"Dog" is a noun.', answer: true, concept: 'grammar:noun-dog', explanation: 'Dog is a noun because it names an animal.' },
        { text: '"Running" is a noun.', answer: false, concept: 'grammar:noun-running', explanation: 'Running is a verb because it describes an action.' },
        { text: '"School" is a noun.', answer: true, concept: 'grammar:noun-school', explanation: 'School is a noun because it names a place.' },
        { text: '"Beautiful" is a noun.', answer: false, concept: 'grammar:noun-beautiful', explanation: 'Beautiful is an adjective because it describes something.' },
        { text: '"Teacher" is a noun.', answer: true, concept: 'grammar:noun-teacher', explanation: 'Teacher is a noun because it names a person.' },
        { text: '"Quickly" is a noun.', answer: false, concept: 'grammar:noun-quickly', explanation: 'Quickly is an adverb because it describes how something is done.' },
        { text: '"Happiness" is a noun.', answer: true, concept: 'grammar:noun-happiness', explanation: 'Happiness is a noun because it names a feeling or idea.' },
        { text: '"Jump" is a noun.', answer: false, concept: 'grammar:noun-jump', explanation: 'Jump is a verb because it describes an action.' },
        { text: '"Mountain" is a noun.', answer: true, concept: 'grammar:noun-mountain', explanation: 'Mountain is a noun because it names a place or thing.' },
        { text: '"Bright" is a noun.', answer: false, concept: 'grammar:noun-bright', explanation: 'Bright is an adjective because it describes something.' },
      ],
    },
    rewards: { starsPerCorrect: 1, bonusThreshold: 8, bonusStars: 3 },
    threeR: { measuresRetention: true, measuresRecall: true, measuresRegistration: true },
  },

  // 7. Articles: A, An, The (fill-blank)
  {
    id: 'eng-fill-articles-22',
    title: 'Articles: A, An, The',
    category: 'english',
    subcategory: 'grammar',
    template: 'fill-blank',
    ageRange: [6, 8],
    difficulty: 2,
    icon: 'pencil',
    color: '#FF6B6B',
    estimatedMinutes: 4,
    questionsPerSession: 8,
    learningObjectives: ['grammar', 'articles', 'sentence-structure'],
    tags: ['grammar', 'articles'],
    content: {
      questions: [
        { text: 'I saw ___ elephant at the zoo.', blank: 'an', options: ['a', 'an', 'the'], concept: 'grammar:article-an-elephant', hint: 'Use "an" before words that start with a vowel sound' },
        { text: '___ sun is shining brightly today.', blank: 'The', options: ['A', 'An', 'The'], concept: 'grammar:article-the-sun', hint: 'Use "the" when talking about something specific everyone knows' },
        { text: 'She ate ___ banana for breakfast.', blank: 'a', options: ['a', 'an', 'the'], concept: 'grammar:article-a-banana', hint: 'Use "a" before words that start with a consonant sound' },
        { text: 'He is ___ honest boy.', blank: 'an', options: ['a', 'an', 'the'], concept: 'grammar:article-an-honest', hint: 'The "h" in "honest" is silent, so it starts with a vowel sound' },
        { text: 'I need ___ umbrella because it is raining.', blank: 'an', options: ['a', 'an', 'the'], concept: 'grammar:article-an-umbrella', hint: 'Use "an" before words that start with a vowel sound' },
        { text: 'There is ___ cat sleeping on the couch.', blank: 'a', options: ['a', 'an', 'the'], concept: 'grammar:article-a-cat', hint: 'Use "a" when mentioning something for the first time' },
        { text: 'Please close ___ door behind you.', blank: 'the', options: ['a', 'an', 'the'], concept: 'grammar:article-the-door', hint: 'Use "the" when both people know which door you mean' },
        { text: 'I want to be ___ astronaut when I grow up.', blank: 'an', options: ['a', 'an', 'the'], concept: 'grammar:article-an-astronaut', hint: 'Use "an" before words that start with a vowel sound' },
      ],
    },
    rewards: { starsPerCorrect: 1, bonusThreshold: 8, bonusStars: 3 },
    threeR: { measuresRetention: true, measuresRecall: true, measuresRegistration: true },
  },

  // 8. Easy Synonyms (match-pairs)
  {
    id: 'eng-syn-easy-25',
    title: 'Easy Synonyms',
    category: 'english',
    subcategory: 'vocabulary',
    template: 'match-pairs',
    ageRange: [5, 7],
    difficulty: 1,
    icon: 'swap-horizontal',
    color: '#FF6B6B',
    estimatedMinutes: 3,
    questionsPerSession: 8,
    learningObjectives: ['vocabulary', 'synonyms'],
    tags: ['synonyms', 'word-meaning'],
    content: {
      pairs: [
        { left: 'happy', right: 'glad', concept: 'synonym:happy-glad' },
        { left: 'big', right: 'large', concept: 'synonym:big-large' },
        { left: 'fast', right: 'quick', concept: 'synonym:fast-quick' },
        { left: 'smart', right: 'clever', concept: 'synonym:smart-clever' },
        { left: 'small', right: 'tiny', concept: 'synonym:small-tiny' },
        { left: 'pretty', right: 'beautiful', concept: 'synonym:pretty-beautiful' },
        { left: 'start', right: 'begin', concept: 'synonym:start-begin' },
        { left: 'end', right: 'finish', concept: 'synonym:end-finish' },
      ],
    },
    rewards: { starsPerCorrect: 1, bonusThreshold: 8, bonusStars: 3 },
    threeR: { measuresRetention: true, measuresRecall: true, measuresRegistration: true },
  },

  // 9. Sight Word Speed (timed-rush)
  {
    id: 'eng-rush-sight-28',
    title: 'Sight Word Speed',
    category: 'english',
    subcategory: 'reading',
    template: 'timed-rush',
    ageRange: [5, 7],
    difficulty: 2,
    icon: 'timer',
    color: '#FF6B6B',
    estimatedMinutes: 3,
    questionsPerSession: 15,
    learningObjectives: ['reading', 'sight-words', 'speed'],
    tags: ['sight-words', 'speed', 'reading'],
    content: {
      timeLimit: 45,
      questions: [
        { question: 'What does "happy" mean?', options: ['Feeling good and joyful', 'Feeling tired', 'Feeling hungry'], correctIndex: 0, concept: 'sight:happy' },
        { question: 'What does "above" mean?', options: ['Below something', 'Higher than something', 'Next to something'], correctIndex: 1, concept: 'sight:above' },
        { question: 'What does "friend" mean?', options: ['Someone you do not know', 'Someone you like and play with', 'A type of animal'], correctIndex: 1, concept: 'sight:friend' },
        { question: 'What does "begin" mean?', options: ['To stop', 'To start', 'To sleep'], correctIndex: 1, concept: 'sight:begin' },
        { question: 'What does "carry" mean?', options: ['To hold and move something', 'To throw something', 'To eat something'], correctIndex: 0, concept: 'sight:carry' },
        { question: 'What does "different" mean?', options: ['The same', 'Not the same', 'Very big'], correctIndex: 1, concept: 'sight:different' },
        { question: 'What does "enough" mean?', options: ['Too little', 'As much as needed', 'Way too much'], correctIndex: 1, concept: 'sight:enough' },
        { question: 'What does "found" mean?', options: ['Lost something', 'Discovered something', 'Broke something'], correctIndex: 1, concept: 'sight:found' },
        { question: 'What does "group" mean?', options: ['One person alone', 'Several things together', 'A type of food'], correctIndex: 1, concept: 'sight:group' },
        { question: 'What does "important" mean?', options: ['Not needed at all', 'Matters a lot', 'Very small'], correctIndex: 1, concept: 'sight:important' },
        { question: 'What does "kind" mean?', options: ['Mean and rude', 'Nice and helpful', 'Loud and noisy'], correctIndex: 1, concept: 'sight:kind' },
        { question: 'What does "listen" mean?', options: ['To talk loudly', 'To pay attention with your ears', 'To close your eyes'], correctIndex: 1, concept: 'sight:listen' },
        { question: 'What does "mountain" mean?', options: ['A flat field', 'A deep hole', 'A very high piece of land'], correctIndex: 2, concept: 'sight:mountain' },
        { question: 'What does "never" mean?', options: ['Always', 'Sometimes', 'Not at any time'], correctIndex: 2, concept: 'sight:never' },
        { question: 'What does "only" mean?', options: ['Many of something', 'Just one and no more', 'All of them'], correctIndex: 1, concept: 'sight:only' },
      ],
    },
    rewards: { starsPerCorrect: 1, bonusThreshold: 12, bonusStars: 3 },
    threeR: { measuresRetention: true, measuresRecall: true, measuresRegistration: true },
  },

  // 10. The Lost Puppy (story-builder)
  {
    id: 'eng-story-adventure-30',
    title: 'The Lost Puppy',
    category: 'english',
    subcategory: 'story',
    template: 'story-builder',
    ageRange: [5, 8],
    difficulty: 2,
    icon: 'dog',
    color: '#FF6B6B',
    estimatedMinutes: 6,
    questionsPerSession: 8,
    learningObjectives: ['reading', 'comprehension', 'moral-values', 'decision-making'],
    tags: ['story', 'adventure', 'kindness', 'responsibility'],
    content: {
      story: {
        start: 'park-discovery',
        scenes: {
          'park-discovery': {
            text: 'You are walking through the park on a sunny afternoon when you hear a soft whimper coming from behind a bush. You peek and see a small, scared puppy with no collar. It looks lost and hungry. What do you do?',
            icon: 'dog',
            choices: [
              { text: 'Go closer and try to help the puppy', nextScene: 'approach-puppy', isGood: true, concept: 'story:kindness-approach' },
              { text: 'Walk away and ignore it', nextScene: 'walk-away', isGood: false, concept: 'story:ignore-need' },
            ],
          },
          'approach-puppy': {
            text: 'You kneel down slowly and speak softly. The puppy wags its tiny tail and comes to you. It is shivering and looks thirsty. You notice a water fountain nearby and a snack in your bag. What do you do first?',
            icon: 'hand-heart',
            choices: [
              { text: 'Give the puppy some water', nextScene: 'give-water', isGood: true, concept: 'story:caring-water' },
              { text: 'Share your snack with the puppy', nextScene: 'give-food', isGood: true, concept: 'story:caring-food' },
            ],
          },
          'walk-away': {
            text: 'You walk away, but you cannot stop thinking about the little puppy all alone. Your heart feels heavy. You decide to go back. When you return, the puppy is still there, looking even more scared. You realize it is always better to help when you can.',
            icon: 'heart-broken',
            choices: [
              { text: 'Go to the puppy and help it now', nextScene: 'approach-puppy', isGood: true, concept: 'story:second-chance-kindness' },
            ],
          },
          'give-water': {
            text: 'You cup your hands under the fountain and bring water to the puppy. It drinks happily and licks your fingers. The puppy already trusts you! Now you need to figure out what to do next.',
            icon: 'water',
            choices: [
              { text: 'Take the puppy to the animal shelter nearby', nextScene: 'shelter', isGood: true, concept: 'story:responsibility-shelter' },
              { text: 'Ask people in the park if they know the puppy', nextScene: 'ask-around', isGood: true, concept: 'story:responsibility-ask' },
            ],
          },
          'give-food': {
            text: 'You break off a piece of your sandwich and offer it gently. The puppy eats eagerly and then nuzzles your hand. It seems a little stronger now. But the puppy still needs a safe home. What should you do?',
            icon: 'food',
            choices: [
              { text: 'Take the puppy to the animal shelter nearby', nextScene: 'shelter', isGood: true, concept: 'story:responsibility-shelter' },
              { text: 'Ask people in the park if they know the puppy', nextScene: 'ask-around', isGood: true, concept: 'story:responsibility-ask' },
            ],
          },
          'ask-around': {
            text: 'You carry the puppy and ask several people in the park. An elderly woman smiles and says, "I think that puppy belongs to the Johnsons on Maple Street. They have been looking everywhere for it!" What do you do?',
            icon: 'account-question',
            choices: [
              { text: 'Take the puppy to the Johnsons on Maple Street', nextScene: 'find-owner', isGood: true, concept: 'story:honesty-return' },
              { text: 'Take the puppy to the shelter just to be safe', nextScene: 'shelter', isGood: true, concept: 'story:caution-shelter' },
            ],
          },
          'shelter': {
            text: 'You bring the puppy to the Happy Paws Animal Shelter. The kind worker there scans the puppy and finds a microchip! They call the owners right away. The Johnson family arrives in minutes, so happy to see their puppy named Biscuit.',
            icon: 'hospital-building',
            choices: [
              { text: 'Continue the story', nextScene: 'happy-ending', isGood: true, concept: 'story:shelter-reunion' },
            ],
          },
          'find-owner': {
            text: 'You walk to Maple Street with the puppy in your arms. When you knock on the Johnsons\' door, a little girl opens it and shouts, "Biscuit! You found Biscuit!" She hugs the puppy tightly. Her parents come out with tears of joy.',
            icon: 'home-heart',
            choices: [
              { text: 'Continue the story', nextScene: 'happy-ending', isGood: true, concept: 'story:direct-reunion' },
            ],
          },
          'happy-ending': {
            text: 'The Johnson family is so grateful that they invite you over for cookies and lemonade. Little Biscuit curls up at your feet, happy and safe. You learned that being kind and responsible can make a real difference. The End.',
            icon: 'star',
            choices: [],
          },
        },
      },
    },
    rewards: { starsPerCorrect: 1, bonusThreshold: 8, bonusStars: 3 },
    threeR: { measuresRetention: true, measuresRecall: true, measuresRegistration: true },
  },
];

// ============================================================================
// MATH GAMES (10)
// ============================================================================

const MATH_GAMES = [
  // 1. Addition Within 10 (multiple-choice)
  {
    id: 'math-add-10-31',
    title: 'Addition Within 10',
    category: 'math',
    subcategory: 'addition',
    template: 'multiple-choice',
    ageRange: [4, 6],
    difficulty: 1,
    icon: 'plus-circle',
    color: '#4ECDC4',
    estimatedMinutes: 4,
    questionsPerSession: 10,
    learningObjectives: ['addition', 'number-sense'],
    tags: ['numbers', 'basic-math'],
    content: {
      questions: [
        { question: '3 + 4 = ?', options: ['5', '6', '7', '8'], correctIndex: 2, concept: 'add:3+4', hint: 'Start at 3 and count up 4 more' },
        { question: '2 + 5 = ?', options: ['6', '7', '8', '9'], correctIndex: 1, concept: 'add:2+5', hint: 'Start at 2 and count up 5 more' },
        { question: '1 + 6 = ?', options: ['5', '6', '7', '8'], correctIndex: 2, concept: 'add:1+6', hint: 'Start at 1 and count up 6 more' },
        { question: '4 + 3 = ?', options: ['5', '6', '7', '8'], correctIndex: 2, concept: 'add:4+3', hint: 'Start at 4 and count up 3 more' },
        { question: '5 + 2 = ?', options: ['6', '7', '8', '9'], correctIndex: 1, concept: 'add:5+2', hint: 'Start at 5 and count up 2 more' },
        { question: '6 + 3 = ?', options: ['7', '8', '9', '10'], correctIndex: 2, concept: 'add:6+3', hint: 'Start at 6 and count up 3 more' },
        { question: '2 + 2 = ?', options: ['3', '4', '5', '6'], correctIndex: 1, concept: 'add:2+2', hint: 'Two plus two makes a pair of pairs' },
        { question: '1 + 8 = ?', options: ['7', '8', '9', '10'], correctIndex: 2, concept: 'add:1+8', hint: 'Start at 1 and count up 8 more' },
        { question: '5 + 5 = ?', options: ['8', '9', '10', '11'], correctIndex: 2, concept: 'add:5+5', hint: 'Two hands with 5 fingers each' },
        { question: '3 + 6 = ?', options: ['7', '8', '9', '10'], correctIndex: 2, concept: 'add:3+6', hint: 'Start at 3 and count up 6 more' },
      ],
    },
    rewards: { starsPerCorrect: 1, bonusThreshold: 8, bonusStars: 3 },
    threeR: { measuresRetention: true, measuresRecall: true, measuresRegistration: true },
  },

  // 2. Subtraction Within 10 (multiple-choice)
  {
    id: 'math-sub-10-34',
    title: 'Subtraction Within 10',
    category: 'math',
    subcategory: 'subtraction',
    template: 'multiple-choice',
    ageRange: [4, 6],
    difficulty: 1,
    icon: 'minus-circle',
    color: '#4ECDC4',
    estimatedMinutes: 4,
    questionsPerSession: 10,
    learningObjectives: ['subtraction', 'number-sense'],
    tags: ['numbers', 'basic-math'],
    content: {
      questions: [
        { question: '7 - 3 = ?', options: ['2', '3', '4', '5'], correctIndex: 2, concept: 'sub:7-3', hint: 'Start at 7 and count back 3' },
        { question: '9 - 5 = ?', options: ['2', '3', '4', '5'], correctIndex: 2, concept: 'sub:9-5', hint: 'Start at 9 and count back 5' },
        { question: '6 - 2 = ?', options: ['2', '3', '4', '5'], correctIndex: 2, concept: 'sub:6-2', hint: 'Start at 6 and count back 2' },
        { question: '8 - 4 = ?', options: ['2', '3', '4', '5'], correctIndex: 2, concept: 'sub:8-4', hint: 'Start at 8 and count back 4' },
        { question: '10 - 7 = ?', options: ['1', '2', '3', '4'], correctIndex: 2, concept: 'sub:10-7', hint: 'Start at 10 and count back 7' },
        { question: '5 - 1 = ?', options: ['2', '3', '4', '5'], correctIndex: 2, concept: 'sub:5-1', hint: 'One less than 5' },
        { question: '8 - 6 = ?', options: ['1', '2', '3', '4'], correctIndex: 1, concept: 'sub:8-6', hint: 'Start at 8 and count back 6' },
        { question: '10 - 4 = ?', options: ['4', '5', '6', '7'], correctIndex: 2, concept: 'sub:10-4', hint: 'Start at 10 and count back 4' },
        { question: '7 - 5 = ?', options: ['1', '2', '3', '4'], correctIndex: 1, concept: 'sub:7-5', hint: 'Start at 7 and count back 5' },
        { question: '9 - 3 = ?', options: ['4', '5', '6', '7'], correctIndex: 2, concept: 'sub:9-3', hint: 'Start at 9 and count back 3' },
      ],
    },
    rewards: { starsPerCorrect: 1, bonusThreshold: 8, bonusStars: 3 },
    threeR: { measuresRetention: true, measuresRecall: true, measuresRegistration: true },
  },

  // 3. Times Tables 2-5 (timed-rush)
  {
    id: 'math-mul-easy-37',
    title: 'Times Tables 2-5',
    category: 'math',
    subcategory: 'multiplication',
    template: 'timed-rush',
    ageRange: [6, 8],
    difficulty: 1,
    icon: 'close-circle',
    color: '#4ECDC4',
    estimatedMinutes: 3,
    questionsPerSession: 10,
    learningObjectives: ['multiplication', 'times-tables'],
    tags: ['numbers', 'times-tables'],
    content: {
      timeLimit: 60,
      questions: [
        { question: '2 x 3 = ?', options: ['4', '5', '6', '7'], correctIndex: 2, concept: 'mul:2x3' },
        { question: '3 x 4 = ?', options: ['10', '11', '12', '13'], correctIndex: 2, concept: 'mul:3x4' },
        { question: '5 x 2 = ?', options: ['8', '9', '10', '11'], correctIndex: 2, concept: 'mul:5x2' },
        { question: '4 x 5 = ?', options: ['18', '19', '20', '21'], correctIndex: 2, concept: 'mul:4x5' },
        { question: '3 x 3 = ?', options: ['6', '7', '8', '9'], correctIndex: 3, concept: 'mul:3x3' },
        { question: '2 x 7 = ?', options: ['12', '13', '14', '15'], correctIndex: 2, concept: 'mul:2x7' },
        { question: '5 x 5 = ?', options: ['20', '23', '25', '27'], correctIndex: 2, concept: 'mul:5x5' },
        { question: '4 x 3 = ?', options: ['10', '11', '12', '13'], correctIndex: 2, concept: 'mul:4x3' },
        { question: '2 x 9 = ?', options: ['16', '17', '18', '19'], correctIndex: 2, concept: 'mul:2x9' },
        { question: '5 x 4 = ?', options: ['18', '19', '20', '21'], correctIndex: 2, concept: 'mul:5x4' },
      ],
    },
    rewards: { starsPerCorrect: 1, bonusThreshold: 8, bonusStars: 3 },
    threeR: { measuresRetention: true, measuresRecall: true, measuresRegistration: true },
  },

  // 4. Basic Division (fill-blank)
  {
    id: 'math-div-basic-40',
    title: 'Basic Division',
    category: 'math',
    subcategory: 'division',
    template: 'fill-blank',
    ageRange: [7, 9],
    difficulty: 2,
    icon: 'division',
    color: '#4ECDC4',
    estimatedMinutes: 5,
    questionsPerSession: 10,
    learningObjectives: ['division', 'number-sense'],
    tags: ['numbers', 'division'],
    content: {
      questions: [
        { text: '12 \u00f7 3 = ___', blank: '4', options: ['3', '4', '5', '6'], concept: 'div:12/3', hint: 'How many groups of 3 make 12?' },
        { text: '15 \u00f7 5 = ___', blank: '3', options: ['2', '3', '4', '5'], concept: 'div:15/5', hint: 'How many groups of 5 make 15?' },
        { text: '20 \u00f7 4 = ___', blank: '5', options: ['4', '5', '6', '7'], concept: 'div:20/4', hint: 'How many groups of 4 make 20?' },
        { text: '18 \u00f7 6 = ___', blank: '3', options: ['2', '3', '4', '5'], concept: 'div:18/6', hint: 'How many groups of 6 make 18?' },
        { text: '24 \u00f7 8 = ___', blank: '3', options: ['2', '3', '4', '5'], concept: 'div:24/8', hint: 'How many groups of 8 make 24?' },
        { text: '16 \u00f7 4 = ___', blank: '4', options: ['3', '4', '5', '6'], concept: 'div:16/4', hint: 'How many groups of 4 make 16?' },
        { text: '21 \u00f7 7 = ___', blank: '3', options: ['2', '3', '4', '5'], concept: 'div:21/7', hint: 'How many groups of 7 make 21?' },
        { text: '30 \u00f7 5 = ___', blank: '6', options: ['5', '6', '7', '8'], concept: 'div:30/5', hint: 'How many groups of 5 make 30?' },
        { text: '36 \u00f7 9 = ___', blank: '4', options: ['3', '4', '5', '6'], concept: 'div:36/9', hint: 'How many groups of 9 make 36?' },
        { text: '28 \u00f7 7 = ___', blank: '4', options: ['3', '4', '5', '6'], concept: 'div:28/7', hint: 'How many groups of 7 make 28?' },
      ],
    },
    rewards: { starsPerCorrect: 1, bonusThreshold: 8, bonusStars: 3 },
    threeR: { measuresRetention: true, measuresRecall: true, measuresRegistration: true },
  },

  // 5. Count to 10 (counting)
  {
    id: 'math-count-10-42',
    title: 'Count to 10',
    category: 'math',
    subcategory: 'counting',
    template: 'counting',
    ageRange: [3, 5],
    difficulty: 1,
    icon: 'numeric',
    color: '#4ECDC4',
    estimatedMinutes: 3,
    questionsPerSession: 8,
    learningObjectives: ['counting', 'number-recognition'],
    tags: ['numbers', 'counting', 'beginner'],
    content: {
      rounds: [
        { count: 3, icon: 'star', color: '#FFD700', concept: 'count:objects-3', label: 'stars' },
        { count: 5, icon: 'apple', color: '#FF6347', concept: 'count:objects-5', label: 'apples' },
        { count: 4, icon: 'heart', color: '#FF69B4', concept: 'count:objects-4', label: 'hearts' },
        { count: 7, icon: 'flower', color: '#FF8C00', concept: 'count:objects-7', label: 'flowers' },
        { count: 6, icon: 'fish', color: '#4682B4', concept: 'count:objects-6', label: 'fish' },
        { count: 8, icon: 'cat', color: '#8B4513', concept: 'count:objects-8', label: 'cats' },
        { count: 9, icon: 'bird', color: '#20B2AA', concept: 'count:objects-9', label: 'birds' },
        { count: 10, icon: 'circle', color: '#9370DB', concept: 'count:objects-10', label: 'circles' },
      ],
    },
    rewards: { starsPerCorrect: 1, bonusThreshold: 6, bonusStars: 3 },
    threeR: { measuresRetention: true, measuresRecall: true, measuresRegistration: true },
  },

  // 6. 2D Shapes Basic (multiple-choice)
  {
    id: 'math-shape-2d-45',
    title: '2D Shapes Basic',
    category: 'math',
    subcategory: 'shapes',
    template: 'multiple-choice',
    ageRange: [4, 6],
    difficulty: 1,
    icon: 'shape',
    color: '#4ECDC4',
    estimatedMinutes: 4,
    questionsPerSession: 8,
    learningObjectives: ['shapes', 'geometry-basics'],
    tags: ['shapes', 'geometry'],
    content: {
      questions: [
        { question: 'How many sides does a triangle have?', options: ['2', '3', '4', '5'], correctIndex: 1, concept: 'shape:triangle-sides', hint: 'Tri means three' },
        { question: 'How many sides does a square have?', options: ['3', '4', '5', '6'], correctIndex: 1, concept: 'shape:square-sides', hint: 'A square has equal sides all around' },
        { question: 'What shape is a ball?', options: ['Square', 'Triangle', 'Circle', 'Rectangle'], correctIndex: 2, concept: 'shape:circle', hint: 'It is perfectly round' },
        { question: 'How many corners does a rectangle have?', options: ['2', '3', '4', '5'], correctIndex: 2, concept: 'shape:rectangle-corners', hint: 'A rectangle is like a stretched square' },
        { question: 'Which shape has no corners?', options: ['Triangle', 'Square', 'Circle', 'Rectangle'], correctIndex: 2, concept: 'shape:circle-corners', hint: 'This shape is perfectly round' },
        { question: 'How many sides does a circle have?', options: ['0', '1', '2', '3'], correctIndex: 0, concept: 'shape:circle-sides', hint: 'A circle is round with no straight edges' },
        { question: 'What shape has 3 corners?', options: ['Circle', 'Triangle', 'Square', 'Rectangle'], correctIndex: 1, concept: 'shape:triangle-corners', hint: 'This shape has 3 sides too' },
        { question: 'Which shape has 4 equal sides?', options: ['Triangle', 'Circle', 'Square', 'Rectangle'], correctIndex: 2, concept: 'shape:square', hint: 'All four sides are the same length' },
      ],
    },
    rewards: { starsPerCorrect: 1, bonusThreshold: 6, bonusStars: 3 },
    threeR: { measuresRetention: true, measuresRecall: true, measuresRegistration: true },
  },

  // 7. Number Sequences (fill-blank)
  {
    id: 'math-pattern-num-49',
    title: 'Number Sequences',
    category: 'math',
    subcategory: 'patterns',
    template: 'fill-blank',
    ageRange: [5, 7],
    difficulty: 2,
    icon: 'grid',
    color: '#4ECDC4',
    estimatedMinutes: 5,
    questionsPerSession: 8,
    learningObjectives: ['patterns', 'number-sense', 'sequences'],
    tags: ['patterns', 'numbers'],
    content: {
      questions: [
        { text: '2, 4, 6, 8, ___', blank: '10', options: ['9', '10', '11', '12'], concept: 'pattern:count-by-2', hint: 'Each number goes up by 2' },
        { text: '5, 10, 15, 20, ___', blank: '25', options: ['22', '24', '25', '30'], concept: 'pattern:count-by-5', hint: 'Each number goes up by 5' },
        { text: '10, 20, 30, 40, ___', blank: '50', options: ['45', '50', '55', '60'], concept: 'pattern:count-by-10', hint: 'Each number goes up by 10' },
        { text: '1, 3, 5, 7, ___', blank: '9', options: ['8', '9', '10', '11'], concept: 'pattern:count-by-2-odd', hint: 'These are odd numbers going up by 2' },
        { text: '3, 6, 9, 12, ___', blank: '15', options: ['13', '14', '15', '16'], concept: 'pattern:count-by-3', hint: 'Each number goes up by 3' },
        { text: '4, 8, 12, 16, ___', blank: '20', options: ['18', '19', '20', '22'], concept: 'pattern:count-by-4', hint: 'Each number goes up by 4' },
        { text: '10, 9, 8, 7, ___', blank: '6', options: ['5', '6', '7', '8'], concept: 'pattern:count-down-1', hint: 'The numbers go down by 1' },
        { text: '20, 18, 16, 14, ___', blank: '12', options: ['10', '11', '12', '13'], concept: 'pattern:count-down-2', hint: 'The numbers go down by 2' },
      ],
    },
    rewards: { starsPerCorrect: 1, bonusThreshold: 6, bonusStars: 3 },
    threeR: { measuresRetention: true, measuresRecall: true, measuresRegistration: true },
  },

  // 8. Bigger / Smaller (sequence-order)
  {
    id: 'math-compare-big-51',
    title: 'Bigger / Smaller',
    category: 'math',
    subcategory: 'comparison',
    template: 'sequence-order',
    ageRange: [4, 6],
    difficulty: 1,
    icon: 'scale-balance',
    color: '#4ECDC4',
    estimatedMinutes: 4,
    questionsPerSession: 6,
    learningObjectives: ['comparison', 'number-sense', 'ordering'],
    tags: ['numbers', 'ordering'],
    content: {
      sequences: [
        { items: ['1', '3', '5', '7', '9'], concept: 'compare:order-1-9' },
        { items: ['2', '4', '6', '8', '10'], concept: 'compare:order-2-10' },
        { items: ['1', '2', '5', '7', '9'], concept: 'compare:order-mixed-1' },
        { items: ['3', '4', '6', '8', '10'], concept: 'compare:order-mixed-2' },
        { items: ['1', '3', '6', '8', '10'], concept: 'compare:order-mixed-3' },
        { items: ['2', '5', '6', '7', '9'], concept: 'compare:order-mixed-4' },
      ],
    },
    rewards: { starsPerCorrect: 1, bonusThreshold: 5, bonusStars: 3 },
    threeR: { measuresRetention: true, measuresRecall: true, measuresRegistration: true },
  },

  // 9. Tell Time: Hours (multiple-choice)
  {
    id: 'math-time-hours-56',
    title: 'Tell Time: Hours',
    category: 'math',
    subcategory: 'time',
    template: 'multiple-choice',
    ageRange: [5, 7],
    difficulty: 1,
    icon: 'clock-outline',
    color: '#4ECDC4',
    estimatedMinutes: 4,
    questionsPerSession: 8,
    learningObjectives: ['time-telling', 'hours'],
    tags: ['time', 'clock'],
    content: {
      questions: [
        { question: 'The clock shows 3:00. What time is it?', options: ['2 o\'clock', '3 o\'clock', '4 o\'clock', '5 o\'clock'], correctIndex: 1, concept: 'time:hour-3', hint: 'The short hand points to the 3' },
        { question: 'The clock shows 7:00. What time is it?', options: ['6 o\'clock', '7 o\'clock', '8 o\'clock', '9 o\'clock'], correctIndex: 1, concept: 'time:hour-7', hint: 'The short hand points to the 7' },
        { question: 'The clock shows 12:00. What time is it?', options: ['11 o\'clock', '12 o\'clock', '1 o\'clock', '10 o\'clock'], correctIndex: 1, concept: 'time:hour-12', hint: 'Both hands point to the 12' },
        { question: 'The clock shows 1:00. What time is it?', options: ['12 o\'clock', '1 o\'clock', '2 o\'clock', '3 o\'clock'], correctIndex: 1, concept: 'time:hour-1', hint: 'The short hand points to the 1' },
        { question: 'The clock shows 9:00. What time is it?', options: ['8 o\'clock', '9 o\'clock', '10 o\'clock', '11 o\'clock'], correctIndex: 1, concept: 'time:hour-9', hint: 'The short hand points to the 9' },
        { question: 'The clock shows 5:00. What time is it?', options: ['4 o\'clock', '5 o\'clock', '6 o\'clock', '7 o\'clock'], correctIndex: 1, concept: 'time:hour-5', hint: 'The short hand points to the 5' },
        { question: 'The clock shows 10:00. What time is it?', options: ['9 o\'clock', '10 o\'clock', '11 o\'clock', '12 o\'clock'], correctIndex: 1, concept: 'time:hour-10', hint: 'The short hand points to the 10' },
        { question: 'The clock shows 6:00. What time is it?', options: ['5 o\'clock', '6 o\'clock', '7 o\'clock', '8 o\'clock'], correctIndex: 1, concept: 'time:hour-6', hint: 'The short hand points to the 6' },
      ],
    },
    rewards: { starsPerCorrect: 1, bonusThreshold: 6, bonusStars: 3 },
    threeR: { measuresRetention: true, measuresRecall: true, measuresRegistration: true },
  },

  // 10. Addition Facts Memory (memory-flip)
  {
    id: 'math-memory-add-60',
    title: 'Addition Facts Memory',
    category: 'math',
    subcategory: 'addition',
    template: 'memory-flip',
    ageRange: [5, 8],
    difficulty: 2,
    icon: 'grid',
    color: '#4ECDC4',
    estimatedMinutes: 5,
    questionsPerSession: 8,
    learningObjectives: ['addition', 'memory', 'number-sense'],
    tags: ['numbers', 'memory', 'addition'],
    content: {
      pairs: [
        { id: 'pair-1', front: '3 + 4', match: '7', concept: 'add:3+4' },
        { id: 'pair-2', front: '5 + 3', match: '8', concept: 'add:5+3' },
        { id: 'pair-3', front: '6 + 6', match: '12', concept: 'add:6+6' },
        { id: 'pair-4', front: '9 + 2', match: '11', concept: 'add:9+2' },
        { id: 'pair-5', front: '7 + 5', match: '12', concept: 'add:7+5' },
        { id: 'pair-6', front: '8 + 4', match: '12', concept: 'add:8+4' },
        { id: 'pair-7', front: '2 + 9', match: '11', concept: 'add:2+9' },
        { id: 'pair-8', front: '6 + 7', match: '13', concept: 'add:6+7' },
      ],
      gridColumns: 4,
    },
    rewards: { starsPerCorrect: 1, bonusThreshold: 6, bonusStars: 3 },
    threeR: { measuresRetention: true, measuresRecall: true, measuresRegistration: true },
  },
];

// ============================================================================
// LIFE SKILLS GAMES (10)
// ============================================================================

const LIFE_SKILLS_GAMES = [
  // 1. Morning Routine (sequence-order)
  {
    id: 'life-routine-morning-61',
    title: 'Morning Routine',
    category: 'lifeSkills',
    subcategory: 'daily-routine',
    template: 'sequence-order',
    ageRange: [4, 7],
    difficulty: 1,
    icon: 'weather-sunny',
    color: '#FFE66D',
    estimatedMinutes: 3,
    questionsPerSession: 8,
    learningObjectives: ['daily-routine', 'time-management'],
    tags: ['routine', 'morning'],
    content: {
      sequences: [
        { items: ['Wake up', 'Brush teeth', 'Take a bath', 'Get dressed', 'Eat breakfast', 'Pack bag', 'Wear shoes', 'Go to school'], concept: 'routine:morning' },
      ],
    },
    rewards: { starsPerCorrect: 1, bonusThreshold: 6, bonusStars: 3 },
    threeR: { measuresRetention: true, measuresRecall: true, measuresRegistration: true },
  },

  // 2. Basic Emotions (multiple-choice)
  {
    id: 'life-emotion-basic-64',
    title: 'Basic Emotions',
    category: 'lifeSkills',
    subcategory: 'emotion-recognition',
    template: 'multiple-choice',
    ageRange: [4, 6],
    difficulty: 1,
    icon: 'emoticon-happy',
    color: '#FFE66D',
    estimatedMinutes: 5,
    questionsPerSession: 10,
    learningObjectives: ['emotion-recognition', 'self-awareness'],
    tags: ['emotions', 'feelings', 'basic'],
    content: {
      questions: [
        { question: 'Your friend shares a toy with you. How do you feel?', options: ['Happy', 'Sad', 'Angry', 'Scared'], correctIndex: 0, concept: 'emotion:happy', hint: 'When someone is kind to us, it makes us feel good!' },
        { question: 'You lost your favorite teddy bear. How do you feel?', options: ['Excited', 'Sad', 'Angry', 'Surprised'], correctIndex: 1, concept: 'emotion:sad', hint: 'Losing something we love makes us feel down.' },
        { question: 'Someone took your snack without asking. How do you feel?', options: ['Happy', 'Sleepy', 'Angry', 'Shy'], correctIndex: 2, concept: 'emotion:angry', hint: 'When someone takes our things, it can upset us.' },
        { question: 'You hear a loud thunder at night. How do you feel?', options: ['Happy', 'Proud', 'Bored', 'Scared'], correctIndex: 3, concept: 'emotion:scared', hint: 'Loud noises can be startling and make us feel afraid.' },
        { question: 'You got a gold star on your homework. How do you feel?', options: ['Proud', 'Sad', 'Angry', 'Scared'], correctIndex: 0, concept: 'emotion:proud', hint: 'Doing well makes us feel good about ourselves!' },
        { question: 'Your best friend moved to another city. How do you feel?', options: ['Excited', 'Sad', 'Angry', 'Silly'], correctIndex: 1, concept: 'emotion:sad-missing', hint: 'Missing someone we care about makes us feel down.' },
        { question: 'Your mom gives you a big birthday surprise. How do you feel?', options: ['Angry', 'Bored', 'Surprised', 'Scared'], correctIndex: 2, concept: 'emotion:surprised', hint: 'When something unexpected and nice happens, we feel surprised!' },
        { question: 'You are meeting new kids at school for the first time. How might you feel?', options: ['Angry', 'Shy', 'Proud', 'Bored'], correctIndex: 1, concept: 'emotion:shy', hint: 'New people can make us feel a little nervous.' },
        { question: 'You helped your mom carry the groceries. How do you feel?', options: ['Happy', 'Scared', 'Sad', 'Angry'], correctIndex: 0, concept: 'emotion:happy-helping', hint: 'Helping others makes us feel warm inside!' },
        { question: 'Your pet dog licks your face when you come home. How do you feel?', options: ['Angry', 'Scared', 'Loved', 'Bored'], correctIndex: 2, concept: 'emotion:loved', hint: 'When someone shows affection, we feel loved.' },
      ],
    },
    rewards: { starsPerCorrect: 1, bonusThreshold: 8, bonusStars: 3 },
    threeR: { measuresRetention: true, measuresRecall: true, measuresRegistration: true },
  },

  // 3. Home Safety (true-false)
  {
    id: 'life-safety-home-67',
    title: 'Home Safety',
    category: 'lifeSkills',
    subcategory: 'safety',
    template: 'true-false',
    ageRange: [4, 7],
    difficulty: 1,
    icon: 'shield-check',
    color: '#FFE66D',
    estimatedMinutes: 5,
    questionsPerSession: 10,
    learningObjectives: ['home-safety', 'hazard-awareness'],
    tags: ['safety', 'home'],
    content: {
      statements: [
        { text: 'It is safe to touch a hot stove.', answer: false, concept: 'safety:hot-stove', explanation: 'Never touch a hot stove! It can burn you badly.' },
        { text: 'You should tell an adult if you find medicine on the floor.', answer: true, concept: 'safety:medicine', explanation: 'Always tell a grown-up if you find medicine. Never eat it on your own.' },
        { text: 'Running with scissors is okay if you are careful.', answer: false, concept: 'safety:scissors', explanation: 'Never run with scissors. Walk slowly and hold them pointing down.' },
        { text: 'You should never play with electrical outlets.', answer: true, concept: 'safety:electricity', explanation: 'Electrical outlets are very dangerous. Never stick anything into them.' },
        { text: 'It is safe to climb on tall furniture by yourself.', answer: false, concept: 'safety:climbing', explanation: 'Climbing on furniture can cause it to fall on you. Always ask for help.' },
        { text: 'You should always wash your hands before eating.', answer: true, concept: 'safety:hygiene', explanation: 'Washing hands removes germs and keeps you healthy!' },
        { text: 'It is okay to open the door for strangers when you are home alone.', answer: false, concept: 'safety:strangers-door', explanation: 'Never open the door for strangers. Tell a trusted adult instead.' },
        { text: 'If there is a fire, you should get out of the house quickly.', answer: true, concept: 'safety:fire-escape', explanation: 'In a fire, leave the house immediately and call for help.' },
        { text: 'Playing with matches is fun and safe.', answer: false, concept: 'safety:matches', explanation: 'Matches can start fires. Only adults should use them.' },
        { text: 'You should know your home address in case of emergency.', answer: true, concept: 'safety:address', explanation: 'Knowing your address helps emergency helpers find you.' },
      ],
    },
    rewards: { starsPerCorrect: 1, bonusThreshold: 8, bonusStars: 3 },
    threeR: { measuresRetention: true, measuresRecall: true, measuresRegistration: true },
  },

  // 4. Healthy vs Unhealthy Food (drag-to-zone)
  {
    id: 'life-health-food-70',
    title: 'Healthy vs Unhealthy Food',
    category: 'lifeSkills',
    subcategory: 'healthy-habits',
    template: 'drag-to-zone',
    ageRange: [4, 7],
    difficulty: 1,
    icon: 'food-apple',
    color: '#FFE66D',
    estimatedMinutes: 4,
    questionsPerSession: 12,
    learningObjectives: ['healthy-eating', 'nutrition'],
    tags: ['health', 'food', 'nutrition'],
    content: {
      zones: [
        { id: 'healthy', label: 'Healthy', color: '#27AE60' },
        { id: 'unhealthy', label: 'Unhealthy', color: '#E74C3C' },
      ],
      items: [
        { id: 'apple', label: 'Apple', zone: 'healthy', concept: 'health:fruit' },
        { id: 'candy', label: 'Candy', zone: 'unhealthy', concept: 'health:sweets' },
        { id: 'carrot', label: 'Carrot', zone: 'healthy', concept: 'health:vegetable' },
        { id: 'soda', label: 'Soda', zone: 'unhealthy', concept: 'health:sugary-drink' },
        { id: 'milk', label: 'Milk', zone: 'healthy', concept: 'health:dairy' },
        { id: 'chips', label: 'Chips', zone: 'unhealthy', concept: 'health:junk-food' },
        { id: 'banana', label: 'Banana', zone: 'healthy', concept: 'health:fruit-banana' },
        { id: 'cake', label: 'Cake', zone: 'unhealthy', concept: 'health:sweets-cake' },
        { id: 'broccoli', label: 'Broccoli', zone: 'healthy', concept: 'health:vegetable-broccoli' },
        { id: 'donut', label: 'Donut', zone: 'unhealthy', concept: 'health:sweets-donut' },
        { id: 'eggs', label: 'Eggs', zone: 'healthy', concept: 'health:protein' },
        { id: 'french-fries', label: 'French Fries', zone: 'unhealthy', concept: 'health:fried-food' },
      ],
    },
    rewards: { starsPerCorrect: 1, bonusThreshold: 10, bonusStars: 3 },
    threeR: { measuresRetention: true, measuresRecall: true, measuresRegistration: true },
  },

  // 5. Good Hygiene Habits (drag-to-zone)
  {
    id: 'life-health-hygiene-72',
    title: 'Good Hygiene Habits',
    category: 'lifeSkills',
    subcategory: 'healthy-habits',
    template: 'drag-to-zone',
    ageRange: [4, 7],
    difficulty: 1,
    icon: 'heart-pulse',
    color: '#FFE66D',
    estimatedMinutes: 4,
    questionsPerSession: 10,
    learningObjectives: ['hygiene', 'cleanliness'],
    tags: ['health', 'hygiene', 'cleanliness'],
    content: {
      zones: [
        { id: 'good', label: 'Good Hygiene', color: '#2ECC71' },
        { id: 'bad', label: 'Bad Habits', color: '#E74C3C' },
      ],
      items: [
        { id: 'wash-hands', label: 'Washing Hands', zone: 'good', concept: 'hygiene:hand-washing' },
        { id: 'skip-brushing', label: 'Skipping Brushing Teeth', zone: 'bad', concept: 'hygiene:teeth' },
        { id: 'daily-bath', label: 'Taking a Daily Bath', zone: 'good', concept: 'hygiene:bathing' },
        { id: 'dirty-clothes', label: 'Wearing Dirty Clothes', zone: 'bad', concept: 'hygiene:clothing' },
        { id: 'cover-sneeze', label: 'Covering Your Sneeze', zone: 'good', concept: 'hygiene:sneezing' },
        { id: 'bite-nails', label: 'Biting Your Nails', zone: 'bad', concept: 'hygiene:nails' },
        { id: 'comb-hair', label: 'Combing Your Hair', zone: 'good', concept: 'hygiene:grooming' },
        { id: 'share-toothbrush', label: 'Sharing a Toothbrush', zone: 'bad', concept: 'hygiene:sharing-germs' },
        { id: 'clean-nails', label: 'Trimming Your Nails', zone: 'good', concept: 'hygiene:nails-trim' },
        { id: 'skip-bath', label: 'Skipping Bath Day', zone: 'bad', concept: 'hygiene:bathing-skip' },
      ],
    },
    rewards: { starsPerCorrect: 1, bonusThreshold: 8, bonusStars: 3 },
    threeR: { measuresRetention: true, measuresRecall: true, measuresRegistration: true },
  },

  // 6. Greeting People (story-builder)
  {
    id: 'life-manners-greet-73',
    title: 'Greeting People',
    category: 'lifeSkills',
    subcategory: 'manners',
    template: 'story-builder',
    ageRange: [4, 7],
    difficulty: 1,
    icon: 'hand-heart',
    color: '#FFE66D',
    estimatedMinutes: 5,
    questionsPerSession: 8,
    learningObjectives: ['greetings', 'politeness', 'social-skills'],
    tags: ['manners', 'greetings', 'social'],
    content: {
      story: {
        start: 'scene1',
        scenes: {
          scene1: {
            text: 'It is your first day at a new school. You walk into the classroom and see your new teacher smiling at you.',
            icon: 'school',
            choices: [
              { text: 'Say "Good morning, Teacher!" with a smile', nextScene: 'scene2a', isGood: true, concept: 'manners:polite-greeting' },
              { text: 'Walk past without saying anything', nextScene: 'scene2b', isGood: false, concept: 'manners:ignoring' },
            ],
          },
          scene2a: {
            text: 'Your teacher says "Welcome! We are so happy to have you." She introduces you to the class. A kid named Sam waves at you.',
            icon: 'emoticon-happy',
            choices: [
              { text: 'Wave back and say "Hi, I\'m happy to meet you!"', nextScene: 'scene3a', isGood: true, concept: 'manners:friendly-intro' },
              { text: 'Look away and ignore Sam', nextScene: 'scene3b', isGood: false, concept: 'manners:ignoring-peer' },
            ],
          },
          scene2b: {
            text: 'The teacher looks a little sad. She still says "Welcome!" but you didn\'t make a good first impression.',
            icon: 'emoticon-sad',
            choices: [
              { text: 'Say "Sorry, good morning Teacher!" and smile', nextScene: 'scene2a', isGood: true, concept: 'manners:apologize-greet' },
              { text: 'Just sit down without saying anything', nextScene: 'scene_end_sad', isGood: false, concept: 'manners:continued-rudeness' },
            ],
          },
          scene3a: {
            text: 'Sam is excited to be your friend! At lunch, Sam introduces you to more kids. Everyone is friendly because you were kind first.',
            icon: 'emoticon-happy',
            choices: [
              { text: 'Say "Thank you for being so nice to me!"', nextScene: 'scene4a', isGood: true, concept: 'manners:gratitude' },
            ],
          },
          scene3b: {
            text: 'Sam looks disappointed. You sit alone at your desk. It feels a little lonely without any friends.',
            icon: 'emoticon-sad',
            choices: [
              { text: 'Go to Sam and say "Hi, sorry I was shy. Can we be friends?"', nextScene: 'scene4a', isGood: true, concept: 'manners:making-amends' },
              { text: 'Stay alone and don\'t talk to anyone', nextScene: 'scene_end_sad', isGood: false, concept: 'manners:isolation' },
            ],
          },
          scene4a: {
            text: 'You made wonderful friends on your first day! Being polite and greeting people kindly helped you feel welcome. Great job!',
            icon: 'emoticon-happy',
            choices: [],
          },
          scene_end_sad: {
            text: 'The day ends and you feel lonely. Tomorrow, try greeting people with a smile. A simple "hello" can make a big difference!',
            icon: 'emoticon-sad',
            choices: [],
          },
        },
      },
    },
    rewards: { starsPerCorrect: 1, bonusThreshold: 6, bonusStars: 3 },
    threeR: { measuresRetention: true, measuresRecall: true, measuresRegistration: true },
  },

  // 7. Recycle Sorting (drag-to-zone)
  {
    id: 'life-env-recycle-75',
    title: 'Recycle Sorting',
    category: 'lifeSkills',
    subcategory: 'environment',
    template: 'drag-to-zone',
    ageRange: [5, 8],
    difficulty: 2,
    icon: 'recycle',
    color: '#FFE66D',
    estimatedMinutes: 5,
    questionsPerSession: 12,
    learningObjectives: ['recycling', 'environment-care'],
    tags: ['environment', 'recycling', 'sorting'],
    content: {
      zones: [
        { id: 'paper', label: 'Paper', color: '#3498DB' },
        { id: 'plastic', label: 'Plastic', color: '#F39C12' },
        { id: 'glass', label: 'Glass', color: '#2ECC71' },
      ],
      items: [
        { id: 'newspaper', label: 'Newspaper', zone: 'paper', concept: 'environment:paper-recycle' },
        { id: 'water-bottle', label: 'Water Bottle', zone: 'plastic', concept: 'environment:plastic-recycle' },
        { id: 'jam-jar', label: 'Jam Jar', zone: 'glass', concept: 'environment:glass-recycle' },
        { id: 'cardboard-box', label: 'Cardboard Box', zone: 'paper', concept: 'environment:paper-cardboard' },
        { id: 'shampoo-bottle', label: 'Shampoo Bottle', zone: 'plastic', concept: 'environment:plastic-shampoo' },
        { id: 'juice-jar', label: 'Juice Jar', zone: 'glass', concept: 'environment:glass-juice' },
        { id: 'notebook', label: 'Old Notebook', zone: 'paper', concept: 'environment:paper-notebook' },
        { id: 'yogurt-cup', label: 'Yogurt Cup', zone: 'plastic', concept: 'environment:plastic-yogurt' },
        { id: 'sauce-bottle', label: 'Sauce Bottle', zone: 'glass', concept: 'environment:glass-sauce' },
        { id: 'magazine', label: 'Magazine', zone: 'paper', concept: 'environment:paper-magazine' },
        { id: 'milk-jug', label: 'Milk Jug', zone: 'plastic', concept: 'environment:plastic-milk' },
        { id: 'pickle-jar', label: 'Pickle Jar', zone: 'glass', concept: 'environment:glass-pickle' },
      ],
    },
    rewards: { starsPerCorrect: 1, bonusThreshold: 10, bonusStars: 3 },
    threeR: { measuresRetention: true, measuresRecall: true, measuresRegistration: true },
  },

  // 8. Basic First Aid (simulation)
  {
    id: 'life-firstaid-basic-77',
    title: 'Basic First Aid',
    category: 'lifeSkills',
    subcategory: 'first-aid',
    template: 'simulation',
    ageRange: [6, 9],
    difficulty: 2,
    icon: 'heart-pulse',
    color: '#FFE66D',
    estimatedMinutes: 5,
    questionsPerSession: 8,
    learningObjectives: ['first-aid', 'wound-care'],
    tags: ['first-aid', 'health', 'safety'],
    content: {
      scenario: {
        title: 'Help! Someone Fell Down',
        concept: 'firstaid:wound-care',
        startingMoney: 0,
        items: [
          { name: 'Stay calm and check on them', price: 0, icon: 'heart-pulse', isGood: true, feedback: 'Great! Staying calm is the first step in any emergency.' },
          { name: 'Panic and run away', price: 0, icon: 'emoticon-sad', isGood: false, feedback: 'Running away does not help. Stay calm and try to help.' },
          { name: 'Clean the wound with water', price: 0, icon: 'water', isGood: true, feedback: 'Rinsing with clean water helps remove dirt from the wound.' },
          { name: 'Put dirt on the wound', price: 0, icon: 'emoticon-sad', isGood: false, feedback: 'Dirt has germs! Never put dirt on a wound.' },
          { name: 'Apply a clean bandage', price: 0, icon: 'heart-pulse', isGood: true, feedback: 'A bandage protects the wound and helps it heal.' },
          { name: 'Ignore the wound', price: 0, icon: 'emoticon-sad', isGood: false, feedback: 'Ignoring a wound can lead to infection. Always clean and cover it.' },
          { name: 'Tell an adult what happened', price: 0, icon: 'hand-heart', isGood: true, feedback: 'An adult can help make sure the wound is properly taken care of.' },
          { name: 'Keep it a secret', price: 0, icon: 'emoticon-sad', isGood: false, feedback: 'Always tell a trusted adult if someone is hurt.' },
        ],
        goal: 'Choose all the correct first aid steps to help your friend!',
      },
    },
    rewards: { starsPerCorrect: 1, bonusThreshold: 6, bonusStars: 3 },
    threeR: { measuresRetention: true, measuresRecall: true, measuresRegistration: true },
  },

  // 9. Road Safety (true-false)
  {
    id: 'life-safety-road-68',
    title: 'Road Safety',
    category: 'lifeSkills',
    subcategory: 'safety',
    template: 'true-false',
    ageRange: [5, 8],
    difficulty: 2,
    icon: 'shield-check',
    color: '#FFE66D',
    estimatedMinutes: 5,
    questionsPerSession: 10,
    learningObjectives: ['road-safety', 'traffic-rules'],
    tags: ['safety', 'road', 'traffic'],
    content: {
      statements: [
        { text: 'You should always look both ways before crossing the street.', answer: true, concept: 'safety:look-both-ways', explanation: 'Looking left, right, then left again keeps you safe from cars.' },
        { text: 'It is okay to run across the road if no cars are coming.', answer: false, concept: 'safety:no-running-road', explanation: 'Never run across the road. Always walk carefully even if it looks clear.' },
        { text: 'A red traffic light means you should stop.', answer: true, concept: 'safety:traffic-light-red', explanation: 'Red means stop! Wait until the light turns green before crossing.' },
        { text: 'You can cross the road anywhere you want.', answer: false, concept: 'safety:crosswalk', explanation: 'Always use a crosswalk or zebra crossing to cross the road safely.' },
        { text: 'Wearing a helmet while riding a bike keeps you safe.', answer: true, concept: 'safety:helmet', explanation: 'A helmet protects your head if you fall off your bike.' },
        { text: 'It is safe to play on the road if there is not much traffic.', answer: false, concept: 'safety:no-play-road', explanation: 'Roads are for vehicles, not playing. Always play in parks or yards.' },
        { text: 'You should hold an adult\'s hand when crossing a busy road.', answer: true, concept: 'safety:hold-hand', explanation: 'Holding an adult\'s hand keeps you safe near traffic.' },
        { text: 'A green walking signal means it is safe to cross.', answer: true, concept: 'safety:walk-signal', explanation: 'The green walk signal tells pedestrians it is their turn to cross.' },
        { text: 'You should wear bright clothes when walking near roads at night.', answer: true, concept: 'safety:visibility', explanation: 'Bright or reflective clothes help drivers see you in the dark.' },
        { text: 'It is okay to chase a ball into the street without looking.', answer: false, concept: 'safety:chase-ball', explanation: 'Always stop and look for cars before going into the street, even to get a ball.' },
      ],
    },
    rewards: { starsPerCorrect: 1, bonusThreshold: 8, bonusStars: 3 },
    threeR: { measuresRetention: true, measuresRecall: true, measuresRegistration: true },
  },

  // 10. Making a Sandwich (sequence-order)
  {
    id: 'life-cook-recipe-81',
    title: 'Making a Sandwich',
    category: 'lifeSkills',
    subcategory: 'cooking-basics',
    template: 'sequence-order',
    ageRange: [5, 8],
    difficulty: 1,
    icon: 'chef-hat',
    color: '#FFE66D',
    estimatedMinutes: 3,
    questionsPerSession: 8,
    learningObjectives: ['cooking-basics', 'following-steps'],
    tags: ['cooking', 'recipe', 'sandwich'],
    content: {
      sequences: [
        { items: ['Get bread', 'Add butter', 'Put lettuce', 'Add cheese', 'Put tomato', 'Close bread', 'Cut in half', 'Serve on plate'], concept: 'cooking:sandwich-steps' },
      ],
    },
    rewards: { starsPerCorrect: 1, bonusThreshold: 6, bonusStars: 3 },
    threeR: { measuresRetention: true, measuresRecall: true, measuresRegistration: true },
  },
];

// ============================================================================
// SCIENCE GAMES (15)
// ============================================================================

const SCIENCE_GAMES = [
  // 1. Animal Habitats (drag-to-zone)
  {
    id: 'sci-animal-habitats',
    title: 'Animal Habitats',
    category: 'science',
    subcategory: 'biology',
    template: 'drag-to-zone',
    ageRange: [4, 7],
    difficulty: 1,
    emoji: '\uD83C\uDF33',
    description: 'Sort animals into their correct habitats: Forest, Ocean, Desert, or Arctic.',
    questionCount: 12,
    hasTimer: false,
    icon: 'tree',
    color: '#8BC34A',
    estimatedMinutes: 5,
    questionsPerSession: 12,
    learningObjectives: ['habitats', 'animal-classification', 'ecosystems'],
    tags: ['animals', 'habitats', 'nature', 'ecosystems'],
    content: {
      zones: [
        { id: 'forest', label: 'Forest', color: '#2E7D32' },
        { id: 'ocean', label: 'Ocean', color: '#1565C0' },
        { id: 'desert', label: 'Desert', color: '#F9A825' },
        { id: 'arctic', label: 'Arctic', color: '#B3E5FC' },
      ],
      items: [
        { id: 'deer', label: 'Deer', zone: 'forest', concept: 'habitat:forest-deer' },
        { id: 'owl', label: 'Owl', zone: 'forest', concept: 'habitat:forest-owl' },
        { id: 'squirrel', label: 'Squirrel', zone: 'forest', concept: 'habitat:forest-squirrel' },
        { id: 'dolphin', label: 'Dolphin', zone: 'ocean', concept: 'habitat:ocean-dolphin' },
        { id: 'shark', label: 'Shark', zone: 'ocean', concept: 'habitat:ocean-shark' },
        { id: 'seahorse', label: 'Seahorse', zone: 'ocean', concept: 'habitat:ocean-seahorse' },
        { id: 'camel', label: 'Camel', zone: 'desert', concept: 'habitat:desert-camel' },
        { id: 'scorpion', label: 'Scorpion', zone: 'desert', concept: 'habitat:desert-scorpion' },
        { id: 'rattlesnake', label: 'Rattlesnake', zone: 'desert', concept: 'habitat:desert-rattlesnake' },
        { id: 'polar-bear', label: 'Polar Bear', zone: 'arctic', concept: 'habitat:arctic-polar-bear' },
        { id: 'penguin', label: 'Penguin', zone: 'arctic', concept: 'habitat:arctic-penguin' },
        { id: 'walrus', label: 'Walrus', zone: 'arctic', concept: 'habitat:arctic-walrus' },
      ],
    },
    rewards: { starsPerCorrect: 1, bonusThreshold: 10, bonusStars: 3 },
    threeR: { measuresRetention: true, measuresRecall: true, measuresRegistration: true },
  },

  // 2. Food Chains (drag-to-zone)
  {
    id: 'sci-food-chains',
    title: 'Food Chains',
    category: 'science',
    subcategory: 'biology',
    template: 'drag-to-zone',
    ageRange: [5, 8],
    difficulty: 2,
    emoji: '\uD83E\uDD81',
    description: 'Sort animals by what they eat: Herbivore, Carnivore, or Omnivore.',
    questionCount: 12,
    hasTimer: false,
    icon: 'food-steak',
    color: '#8BC34A',
    estimatedMinutes: 5,
    questionsPerSession: 12,
    learningObjectives: ['food-chains', 'animal-diet', 'classification'],
    tags: ['animals', 'food-chain', 'biology'],
    content: {
      zones: [
        { id: 'herbivore', label: 'Herbivore', color: '#4CAF50' },
        { id: 'carnivore', label: 'Carnivore', color: '#F44336' },
        { id: 'omnivore', label: 'Omnivore', color: '#FF9800' },
      ],
      items: [
        { id: 'cow', label: 'Cow', zone: 'herbivore', concept: 'diet:herbivore-cow' },
        { id: 'rabbit', label: 'Rabbit', zone: 'herbivore', concept: 'diet:herbivore-rabbit' },
        { id: 'giraffe', label: 'Giraffe', zone: 'herbivore', concept: 'diet:herbivore-giraffe' },
        { id: 'horse', label: 'Horse', zone: 'herbivore', concept: 'diet:herbivore-horse' },
        { id: 'lion', label: 'Lion', zone: 'carnivore', concept: 'diet:carnivore-lion' },
        { id: 'eagle', label: 'Eagle', zone: 'carnivore', concept: 'diet:carnivore-eagle' },
        { id: 'shark', label: 'Shark', zone: 'carnivore', concept: 'diet:carnivore-shark' },
        { id: 'wolf', label: 'Wolf', zone: 'carnivore', concept: 'diet:carnivore-wolf' },
        { id: 'bear', label: 'Bear', zone: 'omnivore', concept: 'diet:omnivore-bear' },
        { id: 'pig', label: 'Pig', zone: 'omnivore', concept: 'diet:omnivore-pig' },
        { id: 'chicken', label: 'Chicken', zone: 'omnivore', concept: 'diet:omnivore-chicken' },
        { id: 'raccoon', label: 'Raccoon', zone: 'omnivore', concept: 'diet:omnivore-raccoon' },
      ],
    },
    rewards: { starsPerCorrect: 1, bonusThreshold: 10, bonusStars: 3 },
    threeR: { measuresRetention: true, measuresRecall: true, measuresRegistration: true },
  },

  // 3. Animal Classification (drag-to-zone)
  {
    id: 'sci-animal-classification',
    title: 'Animal Classification',
    category: 'science',
    subcategory: 'biology',
    template: 'drag-to-zone',
    ageRange: [5, 8],
    difficulty: 2,
    emoji: '\uD83D\uDC3E',
    description: 'Sort animals into the correct group: Mammals, Birds, Fish, or Reptiles.',
    questionCount: 12,
    hasTimer: false,
    icon: 'paw',
    color: '#8BC34A',
    estimatedMinutes: 5,
    questionsPerSession: 12,
    learningObjectives: ['animal-groups', 'classification', 'vertebrates'],
    tags: ['animals', 'classification', 'biology'],
    content: {
      zones: [
        { id: 'mammals', label: 'Mammals', color: '#8D6E63' },
        { id: 'birds', label: 'Birds', color: '#42A5F5' },
        { id: 'fish', label: 'Fish', color: '#26C6DA' },
        { id: 'reptiles', label: 'Reptiles', color: '#66BB6A' },
      ],
      items: [
        { id: 'dog', label: 'Dog', zone: 'mammals', concept: 'class:mammal-dog' },
        { id: 'whale', label: 'Whale', zone: 'mammals', concept: 'class:mammal-whale' },
        { id: 'bat', label: 'Bat', zone: 'mammals', concept: 'class:mammal-bat' },
        { id: 'parrot', label: 'Parrot', zone: 'birds', concept: 'class:bird-parrot' },
        { id: 'penguin', label: 'Penguin', zone: 'birds', concept: 'class:bird-penguin' },
        { id: 'eagle', label: 'Eagle', zone: 'birds', concept: 'class:bird-eagle' },
        { id: 'salmon', label: 'Salmon', zone: 'fish', concept: 'class:fish-salmon' },
        { id: 'clownfish', label: 'Clownfish', zone: 'fish', concept: 'class:fish-clownfish' },
        { id: 'swordfish', label: 'Swordfish', zone: 'fish', concept: 'class:fish-swordfish' },
        { id: 'crocodile', label: 'Crocodile', zone: 'reptiles', concept: 'class:reptile-crocodile' },
        { id: 'turtle', label: 'Turtle', zone: 'reptiles', concept: 'class:reptile-turtle' },
        { id: 'lizard', label: 'Lizard', zone: 'reptiles', concept: 'class:reptile-lizard' },
      ],
    },
    rewards: { starsPerCorrect: 1, bonusThreshold: 10, bonusStars: 3 },
    threeR: { measuresRetention: true, measuresRecall: true, measuresRegistration: true },
  },

  // 4. Parts of a Plant (sequence-order)
  {
    id: 'sci-plant-parts',
    title: 'Parts of a Plant',
    category: 'science',
    subcategory: 'botany',
    template: 'sequence-order',
    ageRange: [4, 7],
    difficulty: 1,
    emoji: '\uD83C\uDF31',
    description: 'Order the parts of a plant from the bottom to the top.',
    questionCount: 8,
    hasTimer: false,
    icon: 'flower',
    color: '#8BC34A',
    estimatedMinutes: 3,
    questionsPerSession: 8,
    learningObjectives: ['plant-anatomy', 'sequencing', 'botany'],
    tags: ['plants', 'botany', 'nature'],
    content: {
      sequences: [
        { items: ['Root', 'Stem', 'Branch', 'Leaf', 'Flower', 'Fruit', 'Seed'], concept: 'plant:parts-bottom-to-top' },
        { items: ['Roots absorb water', 'Stem carries water up', 'Leaves make food from sunlight', 'Flower attracts bees', 'Seeds form inside fruit', 'Fruit falls to ground', 'New plant grows from seed'], concept: 'plant:function-sequence' },
      ],
    },
    rewards: { starsPerCorrect: 1, bonusThreshold: 6, bonusStars: 3 },
    threeR: { measuresRetention: true, measuresRecall: true, measuresRegistration: true },
  },

  // 5. Plant Growth Cycle (sequence-order)
  {
    id: 'sci-plant-growth',
    title: 'Plant Growth Cycle',
    category: 'science',
    subcategory: 'botany',
    template: 'sequence-order',
    ageRange: [4, 7],
    difficulty: 1,
    emoji: '\uD83C\uDF3B',
    description: 'Put the stages of plant growth in the correct order, from seed to fruit.',
    questionCount: 8,
    hasTimer: false,
    icon: 'sprout',
    color: '#8BC34A',
    estimatedMinutes: 3,
    questionsPerSession: 8,
    learningObjectives: ['plant-lifecycle', 'sequencing', 'growth'],
    tags: ['plants', 'lifecycle', 'growth'],
    content: {
      sequences: [
        { items: ['Plant a seed in soil', 'Water the seed', 'Seed sprouts a root', 'A tiny shoot appears', 'Stem grows taller', 'Leaves open up', 'Flower blooms', 'Fruit grows'], concept: 'plant:growth-cycle' },
        { items: ['Seed', 'Germination', 'Seedling', 'Young plant', 'Mature plant', 'Flowering', 'Pollination', 'Fruit and seeds'], concept: 'plant:lifecycle-stages' },
      ],
    },
    rewards: { starsPerCorrect: 1, bonusThreshold: 6, bonusStars: 3 },
    threeR: { measuresRetention: true, measuresRecall: true, measuresRegistration: true },
  },

  // 6. Weather Types (match-pairs)
  {
    id: 'sci-weather-types',
    title: 'Weather Types',
    category: 'science',
    subcategory: 'earth-science',
    template: 'match-pairs',
    ageRange: [4, 7],
    difficulty: 1,
    emoji: '\u2600\uFE0F',
    description: 'Match each weather type with what it feels like or looks like.',
    questionCount: 8,
    hasTimer: false,
    icon: 'weather-partly-cloudy',
    color: '#8BC34A',
    estimatedMinutes: 3,
    questionsPerSession: 8,
    learningObjectives: ['weather', 'observation', 'earth-science'],
    tags: ['weather', 'nature', 'seasons'],
    content: {
      pairs: [
        { left: 'Sunny', right: 'Hot and bright', concept: 'weather:sunny' },
        { left: 'Rainy', right: 'Wet and grey', concept: 'weather:rainy' },
        { left: 'Snowy', right: 'Cold and white', concept: 'weather:snowy' },
        { left: 'Windy', right: 'Trees sway and blow', concept: 'weather:windy' },
        { left: 'Cloudy', right: 'Sky is covered', concept: 'weather:cloudy' },
        { left: 'Stormy', right: 'Thunder and lightning', concept: 'weather:stormy' },
        { left: 'Foggy', right: 'Hard to see far', concept: 'weather:foggy' },
        { left: 'Hail', right: 'Ice balls fall', concept: 'weather:hail' },
      ],
    },
    rewards: { starsPerCorrect: 1, bonusThreshold: 6, bonusStars: 3 },
    threeR: { measuresRetention: true, measuresRecall: true, measuresRegistration: true },
  },

  // 7. Seasons (match-pairs)
  {
    id: 'sci-seasons',
    title: 'Seasons',
    category: 'science',
    subcategory: 'earth-science',
    template: 'match-pairs',
    ageRange: [4, 7],
    difficulty: 1,
    emoji: '\uD83C\uDF42',
    description: 'Match each season with something you see or do during that time of year.',
    questionCount: 8,
    hasTimer: false,
    icon: 'weather-sunny',
    color: '#8BC34A',
    estimatedMinutes: 3,
    questionsPerSession: 8,
    learningObjectives: ['seasons', 'earth-science', 'observation'],
    tags: ['seasons', 'nature', 'weather'],
    content: {
      pairs: [
        { left: 'Spring', right: 'Flowers bloom', concept: 'season:spring' },
        { left: 'Summer', right: 'Beach and sunshine', concept: 'season:summer' },
        { left: 'Autumn', right: 'Leaves change color', concept: 'season:autumn' },
        { left: 'Winter', right: 'Snow and cold', concept: 'season:winter' },
        { left: 'Spring', right: 'Baby animals are born', concept: 'season:spring-animals' },
        { left: 'Summer', right: 'Longest days of the year', concept: 'season:summer-daylight' },
        { left: 'Autumn', right: 'Harvest time', concept: 'season:autumn-harvest' },
        { left: 'Winter', right: 'Shortest days of the year', concept: 'season:winter-daylight' },
      ],
    },
    rewards: { starsPerCorrect: 1, bonusThreshold: 6, bonusStars: 3 },
    threeR: { measuresRetention: true, measuresRecall: true, measuresRegistration: true },
  },

  // 8. Planets (multiple-choice)
  {
    id: 'sci-planets',
    title: 'Planets',
    category: 'science',
    subcategory: 'space',
    template: 'multiple-choice',
    ageRange: [5, 9],
    difficulty: 2,
    emoji: '\uD83E\uDE90',
    description: 'Test your knowledge of the solar system and its eight planets.',
    questionCount: 10,
    hasTimer: false,
    icon: 'earth',
    color: '#8BC34A',
    estimatedMinutes: 5,
    questionsPerSession: 10,
    learningObjectives: ['solar-system', 'planets', 'space'],
    tags: ['space', 'planets', 'solar-system'],
    content: {
      questions: [
        { question: 'Which planet is closest to the Sun?', options: ['Venus', 'Mercury', 'Earth', 'Mars'], correctIndex: 1, concept: 'planet:mercury', hint: 'It is the smallest planet and very hot on the sunny side' },
        { question: 'Which planet do we live on?', options: ['Mars', 'Jupiter', 'Earth', 'Venus'], correctIndex: 2, concept: 'planet:earth', hint: 'It is the only planet known to have life' },
        { question: 'Which planet is known as the Red Planet?', options: ['Jupiter', 'Saturn', 'Mars', 'Neptune'], correctIndex: 2, concept: 'planet:mars', hint: 'Its soil is rich in iron oxide, which looks red' },
        { question: 'Which is the largest planet in our solar system?', options: ['Saturn', 'Jupiter', 'Neptune', 'Uranus'], correctIndex: 1, concept: 'planet:jupiter', hint: 'It is so big that over 1,000 Earths could fit inside it' },
        { question: 'Which planet is famous for its beautiful rings?', options: ['Jupiter', 'Uranus', 'Neptune', 'Saturn'], correctIndex: 3, concept: 'planet:saturn', hint: 'Its rings are made of ice and rock' },
        { question: 'Which planet is the hottest in our solar system?', options: ['Mercury', 'Venus', 'Mars', 'Jupiter'], correctIndex: 1, concept: 'planet:venus-temp', hint: 'Its thick atmosphere traps heat like a greenhouse' },
        { question: 'How many planets are in our solar system?', options: ['7', '8', '9', '10'], correctIndex: 1, concept: 'planet:count', hint: 'Pluto was reclassified as a dwarf planet' },
        { question: 'Which planet spins on its side?', options: ['Neptune', 'Saturn', 'Uranus', 'Jupiter'], correctIndex: 2, concept: 'planet:uranus', hint: 'It is the seventh planet from the Sun' },
        { question: 'Which planet is farthest from the Sun?', options: ['Uranus', 'Saturn', 'Pluto', 'Neptune'], correctIndex: 3, concept: 'planet:neptune', hint: 'It is a blue ice giant and the eighth planet' },
        { question: 'What is the name of the star at the center of our solar system?', options: ['Moon', 'Sun', 'Polaris', 'Sirius'], correctIndex: 1, concept: 'planet:sun-star', hint: 'It gives us light and warmth every day' },
      ],
    },
    rewards: { starsPerCorrect: 1, bonusThreshold: 8, bonusStars: 3 },
    threeR: { measuresRetention: true, measuresRecall: true, measuresRegistration: true },
  },

  // 9. Day & Night (multiple-choice)
  {
    id: 'sci-day-night',
    title: 'Day & Night',
    category: 'science',
    subcategory: 'earth-science',
    template: 'multiple-choice',
    ageRange: [4, 7],
    difficulty: 1,
    emoji: '\uD83C\uDF19',
    description: 'Learn about Earth rotation, shadows, the Sun, and the Moon.',
    questionCount: 8,
    hasTimer: false,
    icon: 'weather-night',
    color: '#8BC34A',
    estimatedMinutes: 4,
    questionsPerSession: 8,
    learningObjectives: ['day-night-cycle', 'earth-rotation', 'shadows'],
    tags: ['earth', 'sun', 'moon', 'shadows'],
    content: {
      questions: [
        { question: 'Why do we have day and night?', options: ['The Sun moves around us', 'The Earth spins on its axis', 'The Moon blocks the Sun', 'Clouds cover the Sun at night'], correctIndex: 1, concept: 'daynight:earth-rotation', hint: 'The Earth takes about 24 hours to make one full spin' },
        { question: 'What do we see in the sky during the day?', options: ['Moon', 'Stars', 'Sun', 'Planets'], correctIndex: 2, concept: 'daynight:sun-day', hint: 'It is very bright and gives us warmth' },
        { question: 'What can you see in the night sky?', options: ['Rainbows', 'Stars and Moon', 'Clouds only', 'The Sun'], correctIndex: 1, concept: 'daynight:night-sky', hint: 'They twinkle and glow in the dark sky' },
        { question: 'When is your shadow the longest?', options: ['At noon', 'In the morning or evening', 'At midnight', 'Shadows are always the same'], correctIndex: 1, concept: 'daynight:shadow-length', hint: 'When the Sun is low in the sky, shadows stretch out' },
        { question: 'How long does it take Earth to spin once?', options: ['12 hours', '24 hours', '7 days', '365 days'], correctIndex: 1, concept: 'daynight:rotation-time', hint: 'One full day and night together' },
        { question: 'What causes shadows?', options: ['Wind', 'Rain', 'An object blocking light', 'Cold weather'], correctIndex: 2, concept: 'daynight:shadow-cause', hint: 'Light cannot pass through solid objects' },
        { question: 'Which gives off its own light?', options: ['Moon', 'Earth', 'Sun', 'Mars'], correctIndex: 2, concept: 'daynight:light-source', hint: 'It is a star that burns very brightly' },
        { question: 'When it is daytime where you live, what is it on the other side of the Earth?', options: ['Also daytime', 'Nighttime', 'Always dark', 'Always light'], correctIndex: 1, concept: 'daynight:opposite-side', hint: 'Only one side of Earth faces the Sun at a time' },
      ],
    },
    rewards: { starsPerCorrect: 1, bonusThreshold: 6, bonusStars: 3 },
    threeR: { measuresRetention: true, measuresRecall: true, measuresRegistration: true },
  },

  // 10. Body Organs (puzzle-assemble)
  {
    id: 'sci-body-organs',
    title: 'Body Organs',
    category: 'science',
    subcategory: 'human-body',
    template: 'puzzle-assemble',
    ageRange: [5, 9],
    difficulty: 2,
    emoji: '\u2764\uFE0F',
    description: 'Assemble the major organs of the human body and learn what each one does.',
    questionCount: 8,
    hasTimer: false,
    icon: 'heart-pulse',
    color: '#8BC34A',
    estimatedMinutes: 5,
    questionsPerSession: 8,
    learningObjectives: ['human-body', 'organs', 'anatomy'],
    tags: ['body', 'organs', 'anatomy', 'health'],
    content: {
      puzzles: [
        {
          title: 'Major Body Organs',
          concept: 'body:organs-assembly',
          pieces: [
            { id: 'brain', label: 'Brain - controls thinking and movement' },
            { id: 'heart', label: 'Heart - pumps blood through the body' },
            { id: 'lungs', label: 'Lungs - help you breathe air' },
            { id: 'stomach', label: 'Stomach - breaks down food' },
            { id: 'liver', label: 'Liver - cleans your blood' },
            { id: 'kidneys', label: 'Kidneys - filter waste from blood' },
            { id: 'intestines', label: 'Intestines - absorb nutrients from food' },
            { id: 'skin', label: 'Skin - protects your body from germs' },
          ],
        },
      ],
    },
    rewards: { starsPerCorrect: 1, bonusThreshold: 6, bonusStars: 3 },
    threeR: { measuresRetention: true, measuresRecall: true, measuresRegistration: true },
  },

  // 11. Five Senses (puzzle-assemble)
  {
    id: 'sci-five-senses',
    title: 'Five Senses',
    category: 'science',
    subcategory: 'human-body',
    template: 'puzzle-assemble',
    ageRange: [4, 7],
    difficulty: 1,
    emoji: '\uD83D\uDC41\uFE0F',
    description: 'Learn about sight, hearing, smell, taste, and touch by assembling the five senses.',
    questionCount: 8,
    hasTimer: false,
    icon: 'eye',
    color: '#8BC34A',
    estimatedMinutes: 4,
    questionsPerSession: 8,
    learningObjectives: ['five-senses', 'human-body', 'observation'],
    tags: ['senses', 'body', 'observation'],
    content: {
      puzzles: [
        {
          title: 'The Five Senses',
          concept: 'body:five-senses',
          pieces: [
            { id: 'sight', label: 'Sight - Eyes help you see colors and shapes' },
            { id: 'hearing', label: 'Hearing - Ears help you hear sounds and music' },
            { id: 'smell', label: 'Smell - Nose helps you smell flowers and food' },
            { id: 'taste', label: 'Taste - Tongue helps you taste sweet and sour' },
            { id: 'touch', label: 'Touch - Skin helps you feel hot and cold' },
          ],
        },
        {
          title: 'Match Sense to Example',
          concept: 'body:senses-examples',
          pieces: [
            { id: 'see-rainbow', label: 'Seeing a rainbow - Sight' },
            { id: 'hear-bird', label: 'Hearing a bird sing - Hearing' },
            { id: 'smell-cookie', label: 'Smelling fresh cookies - Smell' },
            { id: 'taste-lemon', label: 'Tasting a sour lemon - Taste' },
            { id: 'touch-ice', label: 'Touching cold ice - Touch' },
          ],
        },
      ],
    },
    rewards: { starsPerCorrect: 1, bonusThreshold: 6, bonusStars: 3 },
    threeR: { measuresRetention: true, measuresRecall: true, measuresRegistration: true },
  },

  // 12. Continents (puzzle-assemble)
  {
    id: 'sci-continents',
    title: 'Continents',
    category: 'science',
    subcategory: 'geography',
    template: 'puzzle-assemble',
    ageRange: [5, 9],
    difficulty: 2,
    emoji: '\uD83C\uDF0D',
    description: 'Assemble the seven continents and learn a fun fact about each one.',
    questionCount: 8,
    hasTimer: false,
    icon: 'earth',
    color: '#8BC34A',
    estimatedMinutes: 5,
    questionsPerSession: 8,
    learningObjectives: ['continents', 'geography', 'world-knowledge'],
    tags: ['continents', 'geography', 'world'],
    content: {
      puzzles: [
        {
          title: 'The 7 Continents',
          concept: 'geo:continents-assembly',
          pieces: [
            { id: 'asia', label: 'Asia - the largest continent' },
            { id: 'africa', label: 'Africa - home to the Sahara Desert' },
            { id: 'north-america', label: 'North America - has the Grand Canyon' },
            { id: 'south-america', label: 'South America - has the Amazon Rainforest' },
            { id: 'europe', label: 'Europe - has the Eiffel Tower' },
            { id: 'australia', label: 'Australia - home to kangaroos' },
            { id: 'antarctica', label: 'Antarctica - the coldest continent' },
          ],
        },
      ],
    },
    rewards: { starsPerCorrect: 1, bonusThreshold: 6, bonusStars: 3 },
    threeR: { measuresRetention: true, measuresRecall: true, measuresRegistration: true },
  },

  // 13. Water Cycle (sequence-order)
  {
    id: 'sci-water-cycle',
    title: 'Water Cycle',
    category: 'science',
    subcategory: 'earth-science',
    template: 'sequence-order',
    ageRange: [5, 8],
    difficulty: 2,
    emoji: '\uD83D\uDCA7',
    description: 'Put the stages of the water cycle in the correct order.',
    questionCount: 8,
    hasTimer: false,
    icon: 'water',
    color: '#8BC34A',
    estimatedMinutes: 4,
    questionsPerSession: 8,
    learningObjectives: ['water-cycle', 'evaporation', 'precipitation'],
    tags: ['water', 'cycle', 'earth-science'],
    content: {
      sequences: [
        { items: ['Sun heats the water', 'Water evaporates into the air', 'Water vapor rises and cools', 'Clouds form from tiny droplets', 'Clouds get heavy with water', 'Rain falls from the clouds', 'Water flows into rivers', 'Rivers carry water to the ocean'], concept: 'water:cycle-full' },
        { items: ['Evaporation', 'Condensation', 'Cloud formation', 'Precipitation', 'Collection'], concept: 'water:cycle-stages' },
      ],
    },
    rewards: { starsPerCorrect: 1, bonusThreshold: 6, bonusStars: 3 },
    threeR: { measuresRetention: true, measuresRecall: true, measuresRegistration: true },
  },

  // 14. Materials & Properties (true-false)
  {
    id: 'sci-materials',
    title: 'Materials & Properties',
    category: 'science',
    subcategory: 'physics',
    template: 'true-false',
    ageRange: [5, 8],
    difficulty: 2,
    emoji: '\uD83E\uDDEA',
    description: 'Decide if statements about everyday materials and their properties are true or false.',
    questionCount: 10,
    hasTimer: false,
    icon: 'flask',
    color: '#8BC34A',
    estimatedMinutes: 5,
    questionsPerSession: 10,
    learningObjectives: ['materials', 'properties', 'states-of-matter'],
    tags: ['materials', 'physics', 'properties'],
    content: {
      statements: [
        { text: 'Wood floats on water.', answer: true, concept: 'material:wood-floats', explanation: 'Most wood is less dense than water, so it floats on the surface.' },
        { text: 'A metal spoon will sink in water.', answer: true, concept: 'material:metal-sinks', explanation: 'Metal is denser than water, so it sinks to the bottom.' },
        { text: 'Glass is a flexible material that bends easily.', answer: false, concept: 'material:glass-rigid', explanation: 'Glass is hard and brittle. It breaks rather than bends.' },
        { text: 'Rubber can stretch and bounce back to its shape.', answer: true, concept: 'material:rubber-elastic', explanation: 'Rubber is elastic, which means it can stretch and return to its original shape.' },
        { text: 'Ice is a solid form of water.', answer: true, concept: 'material:ice-solid', explanation: 'When water freezes at 0 degrees Celsius, it becomes solid ice.' },
        { text: 'A rock will melt if you put it in warm water.', answer: false, concept: 'material:rock-melt', explanation: 'Rocks need extremely high temperatures to melt, much hotter than warm water.' },
        { text: 'Paper is waterproof and does not get wet.', answer: false, concept: 'material:paper-absorbs', explanation: 'Paper absorbs water and becomes soggy when wet.' },
        { text: 'Magnets can attract iron and steel objects.', answer: true, concept: 'material:magnet-attract', explanation: 'Magnets have a special force that pulls iron and steel toward them.' },
        { text: 'Plastic is made from natural materials found in the ground.', answer: true, concept: 'material:plastic-origin', explanation: 'Most plastic is made from oil, which comes from deep underground.' },
        { text: 'Cotton fabric is heavier than a steel chain of the same size.', answer: false, concept: 'material:cotton-light', explanation: 'Cotton is much lighter than steel because it is less dense.' },
      ],
    },
    rewards: { starsPerCorrect: 1, bonusThreshold: 8, bonusStars: 3 },
    threeR: { measuresRetention: true, measuresRecall: true, measuresRegistration: true },
  },

  // 15. Simple Machines (memory-flip)
  {
    id: 'sci-simple-machines',
    title: 'Simple Machines',
    category: 'science',
    subcategory: 'physics',
    template: 'memory-flip',
    ageRange: [5, 9],
    difficulty: 2,
    emoji: '\u2699\uFE0F',
    description: 'Match each simple machine with a real-world example of how it is used.',
    questionCount: 8,
    hasTimer: false,
    icon: 'cog',
    color: '#8BC34A',
    estimatedMinutes: 5,
    questionsPerSession: 8,
    learningObjectives: ['simple-machines', 'physics', 'engineering'],
    tags: ['machines', 'physics', 'engineering'],
    content: {
      pairs: [
        { id: 'lever', front: 'Lever', match: 'Seesaw', concept: 'machine:lever' },
        { id: 'wheel', front: 'Wheel & Axle', match: 'Bicycle', concept: 'machine:wheel-axle' },
        { id: 'pulley', front: 'Pulley', match: 'Flagpole rope', concept: 'machine:pulley' },
        { id: 'inclined-plane', front: 'Inclined Plane', match: 'Ramp', concept: 'machine:inclined-plane' },
        { id: 'wedge', front: 'Wedge', match: 'Axe blade', concept: 'machine:wedge' },
        { id: 'screw', front: 'Screw', match: 'Jar lid', concept: 'machine:screw' },
        { id: 'lever2', front: 'Lever example', match: 'Bottle opener', concept: 'machine:lever-opener' },
        { id: 'wheel2', front: 'Wheel example', match: 'Doorknob', concept: 'machine:wheel-doorknob' },
      ],
      gridColumns: 4,
    },
    rewards: { starsPerCorrect: 1, bonusThreshold: 6, bonusStars: 3 },
    threeR: { measuresRetention: true, measuresRecall: true, measuresRegistration: true },
  },
];

// ============================================================================
// Combined game configs
// ============================================================================

const gameConfigs = [
  ...ENGLISH_GAMES,
  ...ENGLISH_GAMES_EXTRA,
  ...MATH_GAMES,
  ...MATH_GAMES_EXTRA,
  ...LIFE_SKILLS_GAMES,
  ...LIFE_SKILLS_GAMES_EXTRA,
  ...SCIENCE_GAMES,
  ...CREATIVITY_GAMES,
];

export default gameConfigs;

// ─── Helper functions ────────────────────────────────────────────────────────

/**
 * Get a game config by its unique ID.
 * @param {string} id
 * @returns {Object|null}
 */
export function getGameById(id) {
  return gameConfigs.find((g) => g.id === id) || null;
}

/**
 * Get all games in a given category.
 * @param {string} category - 'english' | 'math' | 'lifeSkills' | 'science' | 'creativity' | 'all'
 * @returns {Object[]}
 */
export function getGamesByCategory(category) {
  if (!category || category === 'all') return gameConfigs;
  return gameConfigs.filter((g) => g.category === category);
}

/**
 * Get games by template type.
 * @param {string} template - e.g. 'multiple-choice', 'match-pairs'
 * @returns {Object[]}
 */
export function getGamesByTemplate(template) {
  return gameConfigs.filter((g) => g.template === template);
}

/**
 * Get games suitable for a given age.
 * @param {number} age
 * @returns {Object[]}
 */
export function getGamesForAge(age) {
  return gameConfigs.filter((g) => age >= g.ageRange[0] && age <= g.ageRange[1]);
}

/**
 * Get games filtered by difficulty level.
 * @param {number} difficulty - 1, 2, or 3
 * @returns {Object[]}
 */
export function getGamesByDifficulty(difficulty) {
  return gameConfigs.filter((g) => g.difficulty === difficulty);
}

/**
 * Get games matching multiple criteria.
 * @param {Object} filters - { category, template, difficulty, age, tags }
 * @returns {Object[]}
 */
export function filterGames(filters = {}) {
  let results = gameConfigs;

  if (filters.category && filters.category !== 'all') {
    results = results.filter((g) => g.category === filters.category);
  }
  if (filters.template) {
    results = results.filter((g) => g.template === filters.template);
  }
  if (filters.difficulty) {
    results = results.filter((g) => g.difficulty === filters.difficulty);
  }
  if (filters.age) {
    results = results.filter((g) => filters.age >= g.ageRange[0] && filters.age <= g.ageRange[1]);
  }
  if (filters.tags && filters.tags.length > 0) {
    results = results.filter((g) =>
      filters.tags.some((tag) => g.tags && g.tags.includes(tag))
    );
  }

  return results;
}

// Named category exports for convenience
export {
  ENGLISH_GAMES,
  ENGLISH_GAMES_EXTRA,
  MATH_GAMES,
  MATH_GAMES_EXTRA,
  LIFE_SKILLS_GAMES,
  LIFE_SKILLS_GAMES_EXTRA,
  SCIENCE_GAMES,
  CREATIVITY_GAMES,
};
